<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-aria/autocomplete';
import collectionsDocs from 'docs:@react-types/shared/src/collections.d.ts';
import {FunctionAPI, HeaderInfo, InterfaceType, TypeContext, TypeLink} from '@react-spectrum/docs';
import i18nDocs from 'docs:@react-aria/i18n';
import overlaysDocs from 'docs:@react-aria/overlays';
import packageData from '@react-aria/autocomplete/package.json';
import selectionDocs from 'docs:@react-stately/selection';
import statelyDocs from 'docs:@react-stately/combobox';
import anatomy from 'url:./searchautocomplete-anatomy.png';
import ChevronRight from '@spectrum-icons/workflow/ChevronRight';
import {ExampleCard} from '@react-spectrum/docs/src/ExampleCard';

---
category: Pickers
keywords: [autocomplete, autosuggest, typeahead, search, aria]
after_version: 3.0.0
---

# useSearchAutocomplete

<p>{docs.exports.useSearchAutocomplete.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['useSearchAutocomplete']}
  />

## API

<FunctionAPI function={docs.exports.useSearchAutocomplete} links={docs.links} />

## Features

Autocomplete for search fields can be implemented using the [&lt;datalist&gt;](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/datalist) HTML element, but this has limited functionality and behaves differently across browsers.
`useSearchAutocomplete` helps achieve accessible search field and autocomplete components that can be styled as needed.

* Support for filtering a list of options by typing
* Support for selecting a single option
* Support for disabled options
* Support for groups of items in sections
* Support for custom user input values
* Support for controlled and uncontrolled options, selection, input value, and open state
* Support for custom filter functions
* Async loading and infinite scrolling support
* Support for virtualized scrolling for performance with long lists
* Exposed to assistive technology as a `combobox` with ARIA
* Labeling support for accessibility
* Required and invalid states exposed to assistive technology via ARIA
* Support for mouse, touch, and keyboard interactions
* Keyboard support for opening the list box using the arrow keys, including automatically focusing
  the first or last item accordingly
* Support for opening the list box when typing, on focus, or manually
* Handles virtual clicks on the input from touch screen readers to toggle the list box
* Virtual focus management for list box option navigation
* Hides elements outside the input and list box from assistive technology while the list box is open in a portal
* Custom localized announcements for option focusing, filtering, and selection using an ARIA live region to work around VoiceOver bugs

## Anatomy

<img src={anatomy} alt="Anatomy of useSearchAutocomplete" height={400} style={{display: 'block', margin: '20px auto'}} />

A search autocomplete consists of a label, an input which displays the current value, and a list box popup. Users can type within the input
to see search suggestions within the list box. The list box popup may be opened by a variety of input field interactions specified
by the `menuTrigger` prop provided to `useSearchAutocomplete`. `useSearchAutocomplete` handles exposing
the correct ARIA attributes for accessibility for each of the elements comprising the search autocomplete. It should be combined
with [useListBox](useListBox.html), which handles the implementation of the popup list box.

`useSearchAutocomplete` returns props that you should spread onto the appropriate elements:

<TypeContext.Provider value={docs.links}>
  <InterfaceType properties={docs.links[docs.exports.useSearchAutocomplete.return.base?.id ?? docs.exports.useSearchAutocomplete.return.id].properties} />
</TypeContext.Provider>

State is managed by the <TypeLink links={statelyDocs.links} type={statelyDocs.exports.useComboBoxState} /> hook from `@react-stately/combobox`.
The state object should be passed as an option to `useSearchAutocomplete`.

If the search field does not have a visible label, an `aria-label` or `aria-labelledby` prop must be provided instead to
identify it to assistive technology.

## State management

`useSearchAutocomplete` requires knowledge of the options in order to handle keyboard
navigation and other interactions. It does this using the <TypeLink links={collectionsDocs.links} type={collectionsDocs.exports.Collection} />
interface, which is a generic interface to access sequential unique keyed data. You can
implement this interface yourself, e.g. by using a prop to pass a list of item objects,
but <TypeLink links={statelyDocs.links} type={statelyDocs.exports.useComboBoxState} /> from
`@react-stately/combobox` implements a JSX based interface for building collections instead.
See [Collection Components](/react-stately/collections.html) for more information,
and [Collection Interface](/react-stately/Collection.html) for internal details.

In addition, <TypeLink links={statelyDocs.links} type={statelyDocs.exports.useComboBoxState} />
manages the state necessary for single selection and exposes
a <TypeLink links={selectionDocs.links} type={selectionDocs.exports.SelectionManager} />,
which makes use of the collection to provide an interface to update the selection state.
It also holds state to track if the popup is open, if the search field is focused, and the current input value.
For more information about selection, see [Selection](/react-stately/selection.html).

## Example

This example uses an `<input>` element for the search field.
A "contains" filter function is obtained from <TypeLink links={i18nDocs.links} type={i18nDocs.exports.useFilter} /> and is passed to <TypeLink links={statelyDocs.links} type={statelyDocs.exports.useComboBoxState} /> so
that the list box can be filtered based on the option text and the current input text.

The list box popup should use the same `Popover` and `ListBox` components created with [useOverlay](useOverlay.html)
and [useListBox](useListBox.html) that you may already have in your component library or application. These can be shared with other
components such as a `Select` created with [useSelect](useSelect.html) or a `Dialog` popover created with [useDialog](useDialog.html).
The code for these components is also included below in the collapsed sections.

This example does not do any advanced popover positioning or portaling to escape its visual container.
See [useOverlayTrigger](useOverlayTrigger.html) for an example of how to implement this
using <TypeLink links={overlaysDocs.links} type={overlaysDocs.exports.useOverlayPosition} />.

In addition, see [useListBox](useListBox.html) for examples of sections (option groups), and more complex
options.

```tsx example export=true
import {Item} from '@react-stately/collections';
import {useButton} from '@react-aria/button';
import {useComboBoxState} from '@react-stately/combobox'
import {useSearchAutocomplete} from '@react-aria/autocomplete';
import {useFilter} from '@react-aria/i18n';

// Reuse the ListBox and Popover from your component library. See below for details.
import {ListBox, Popover} from 'your-component-library';

function SearchAutocomplete(props) {
  // Setup filter function and state.
  let {contains} = useFilter({sensitivity: 'base'});
  let state = useComboBoxState({...props, defaultFilter: contains});
  
  // Setup refs and get props for child elements.
  let inputRef = React.useRef(null);
  let listBoxRef = React.useRef(null);
  let popoverRef = React.useRef(null);

  let {inputProps, listBoxProps, labelProps, clearButtonProps} = useSearchAutocomplete(
    {
      ...props,
      popoverRef,
      listBoxRef,
      inputRef,
    },
    state
  );

  let {buttonProps} = useButton(clearButtonProps);

  return (
    <div style={{display: 'inline-flex', flexDirection: 'column'}}>
      <label {...labelProps}>{props.label}</label>
      <div style={{position: 'relative', display: 'inline-block'}}>
        <input
          {...inputProps}
          ref={inputRef}
          style={{
            height: 24,
            boxSizing: 'border-box',
            marginRight: 0,
            fontSize: 16
          }} />
          {state.inputValue !== '' &&
            <button {...buttonProps}>❎</button>
          }
        {state.isOpen &&
          <Popover popoverRef={popoverRef} isOpen={state.isOpen} onClose={state.close}>
            <ListBox
              {...listBoxProps}
              listBoxRef={listBoxRef}
              state={state} />
          </Popover>
        }
      </div>
    </div>
  );
}

<SearchAutocomplete label="Search Animals">
  <Item key="red panda">Red Panda</Item>
  <Item key="cat">Cat</Item>
  <Item key="dog">Dog</Item>
  <Item key="aardvark">Aardvark</Item>
  <Item key="kangaroo">Kangaroo</Item>
  <Item key="snake">Snake</Item>
</SearchAutocomplete>
```

### Popover

The `Popover` component is used to contain the popup listbox for the SearchAutocomplete.
It can be shared between many other components, including [Select](useSelect.html),
[Menu](useMenuTrigger.html), [Dialog](useOverlayTrigger.html), and others.
See [useOverlayTrigger](useOverlayTrigger.html) for more examples of popovers.

<details>
  <summary style={{fontWeight: 'bold'}}><ChevronRight size="S" /> Show code</summary>

```tsx example export=true render=false
import {useOverlay, DismissButton} from '@react-aria/overlays';
import {FocusScope} from '@react-aria/focus';

function Popover(props) {
  let ref = React.useRef();
  let {
    popoverRef = ref,
    isOpen,
    onClose,
    children
  } = props;

  // Handle events that should cause the popup to close,
  // e.g. blur, clicking outside, or pressing the escape key.
  let {overlayProps} = useOverlay({
    isOpen,
    onClose,
    shouldCloseOnBlur: true,
    isDismissable: true
  }, popoverRef);

  // Add a hidden <DismissButton> component at the end of the popover
  // to allow screen reader users to dismiss the popup easily.
  return (
    <FocusScope restoreFocus>
      <div
        {...overlayProps}
        ref={popoverRef}
        style={{
          position: "absolute",
          width: "100%",
          border: "1px solid gray",
          background: "lightgray",
          marginTop: 4
        }}>
        {children}
        <DismissButton onDismiss={onClose} />
      </div>
    </FocusScope>
  );
}
```

</details>

### ListBox

The `ListBox` and `Option` components are used to show the filtered list of options as the
user types in the SearchAutocomplete. They can also be shared with other components like a [Select](useSelect.html). See
[useListBox](useListBox.html) for more examples, including sections and more complex items.

<details>
  <summary style={{fontWeight: 'bold'}}><ChevronRight size="S" /> Show code</summary>

```tsx example export=true render=false
import {useListBox, useOption} from '@react-aria/listbox';

function ListBox(props) {
  let ref = React.useRef();
  let {listBoxRef = ref, state} = props;
  let {listBoxProps} = useListBox(props, state, listBoxRef);

  return (
    <ul
      {...listBoxProps}
      ref={listBoxRef}
      style={{
        margin: 0,
        padding: 0,
        listStyle: "none",
        maxHeight: "150px",
        overflow: "auto"
      }}>
      {[...state.collection].map(item => (
        <Option
          key={item.key}
          item={item}
          state={state} />
      ))}
    </ul>
  );
}

function Option({item, state}) {
  let ref = React.useRef();
  let {optionProps, isSelected, isFocused, isDisabled} = useOption({key: item.key}, state, ref);

  let backgroundColor;
  let color = 'black';

  if (isSelected) {
    backgroundColor = 'blueviolet';
    color = 'white';
  } else if (isFocused) {
    backgroundColor = 'gray';
  } else if (isDisabled) {
    backgroundColor = 'transparent';
    color = 'gray';
  }

  return (
    <li
      {...optionProps}
      ref={ref}
      style={{
        background: backgroundColor,
        color: color,
        padding: '2px 5px',
        outline: 'none',
        cursor: 'pointer'
      }}>
      {item.rendered}
    </li>
  );
}
```

</details>

## Usage

The following examples show how to use the SearchAutocomplete component created in the above example.

### Uncontrolled

The following example shows how you would create an uncontrolled SearchAutocomplete. The input value, selected option, and open state is completely
uncontrolled.

```tsx example
<SearchAutocomplete label="Search Animals">
  <Item key="red panda">Red Panda</Item>
  <Item key="cat">Cat</Item>
  <Item key="dog">Dog</Item>
  <Item key="aardvark">Aardvark</Item>
  <Item key="kangaroo">Kangaroo</Item>
  <Item key="snake">Snake</Item>
</SearchAutocomplete>
```

### Dynamic collections

SearchAutocomplete follows the [Collection Components API](../react-stately/collections.html), accepting both static and dynamic collections.
The examples above show static collections, which can be used when the full list of options is known ahead of time. Dynamic collections,
as shown below, can be used when the options come from an external data source such as an API call, or update over time.

As seen below, an iterable list of options is passed to the SearchAutocomplete using the `defaultItems` prop. The input's value is passed to the
`onSubmit` handler, along with a key if the event was triggered by selecting an item from the listbox.

```tsx example
function Example() {
  let options = [
    {id: 1, name: 'Aerospace'},
    {id: 2, name: 'Mechanical'},
    {id: 3, name: 'Civil'},
    {id: 4, name: 'Biomedical'},
    {id: 5, name: 'Nuclear'},
    {id: 6, name: 'Industrial'},
    {id: 7, name: 'Chemical'},
    {id: 8, name: 'Agricultural'},
    {id: 9, name: 'Electrical'}
  ];
  let [major, setMajor] = React.useState();

  let onSubmit = (value, key) => {
    if (value) {
      setMajor(value);
    } else if (key) {
      setMajor(options.find(o => o.id === key).name);
    }
  };

  return (
    <>
      <SearchAutocomplete
        label="Search engineering majors"
        defaultItems={options}
        onSubmit={onSubmit}>
        {(item) => <Item>{item.name}</Item>}
      </SearchAutocomplete>
      <p>Results for: {major}</p>
    </>
  );
}
```

### Custom filtering

By default, `useComboBoxState` uses the filter function passed to the `defaultFilter` prop (in the above example, a
"contains" function from `useFilter`). The filter function can be overridden by users of the `SearchAutocomplete` component by
using the `items` prop to control the filtered list. When `items` is provided rather than `defaultItems`, `useComboBoxState`
does no filtering of its own.

The following example makes the `inputValue` controlled, and updates the filtered list that is passed to the `items`
prop when the input changes value.

```tsx example
function Example() {
  let options = [
    {id: 1, email: 'fake@email.com'},
    {id: 2, email: 'anotherfake@email.com'},
    {id: 3, email: 'bob@email.com'},
    {id: 4, email: 'joe@email.com'},
    {id: 5, email: 'yourEmail@email.com'},
    {id: 6, email: 'valid@email.com'},
    {id: 7, email: 'spam@email.com'},
    {id: 8, email: 'newsletter@email.com'},
    {id: 9, email: 'subscribe@email.com'}
  ];

  let {startsWith} = useFilter({sensitivity: 'base'});
  let [filterValue, setFilterValue] = React.useState('');
  let filteredItems = React.useMemo(
    () => options.filter((item) => startsWith(item.email, filterValue)),
    [options, filterValue]
  );

  return (
    <SearchAutocomplete
      label="To:"
      items={filteredItems}
      inputValue={filterValue}
      onInputChange={setFilterValue}
      placeholder="Enter recipient email">
      {(item) => <Item>{item.email}</Item>}
    </SearchAutocomplete>
  );
}
```

### Fully controlled

The following example shows how you would create a controlled SearchAutocomplete, by controlling the input value (`inputValue`)
and the autocomplete options (`items`). By passing in `inputValue` and `items` to the `SearchAutocomplete` you can control
exactly what your SearchAutocomplete should display. For example, note that the item filtering for the controlled SearchAutocomplete below now follows a "starts with"
filter strategy, accomplished by controlling the exact set of items available to the SearchAutocomplete whenever the input value updates.

```tsx example
function ControlledSearchAutocomplete() {
  let optionList = [
    {name: 'Red Panda', id: '1'},
    {name: 'Cat', id: '2'},
    {name: 'Dog', id: '3'},
    {name: 'Aardvark', id: '4'},
    {name: 'Kangaroo', id: '5'},
    {name: 'Snake', id: '6'}
  ];

  // Store SearchAutocomplete input value, selected option, open state, and items
  // in a state tracker
  let [fieldState, setFieldState] = React.useState({
    inputValue: '',
    items: optionList
  });

  // Implement custom filtering logic and control what items are
  // available to the SearchAutocomplete.
  let {startsWith} = useFilter({sensitivity: 'base'});

  // Specify how each of the SearchAutocomplete values should change when an
  // option is selected from the list box
  let onSubmit = (value, key) => {
    setFieldState(prevState => {
      let selectedItem = prevState.items.find(option => option.id === key);
      return ({
        inputValue: selectedItem?.name ?? '',
        items: optionList.filter(item => startsWith(item.name, selectedItem?.name ?? ''))
      })
    });
  };

  // Specify how each of the SearchAutocomplete values should change when the input
  // field is altered by the user
  let onInputChange = (value) => {
    setFieldState(prevState => ({
      inputValue: value,
      items: optionList.filter(item => startsWith(item.name, value))
    }));
  };

  // Show entire list if user opens the menu manually
  let onOpenChange = (isOpen, menuTrigger) => {
    if (menuTrigger === 'manual' && isOpen) {
      setFieldState(prevState => ({
        inputValue: prevState.inputValue,
        items: optionList
      }));
    }
  };

  // Pass each controlled prop to useSearchAutocomplete along with their
  // change handlers
  return (
    <SearchAutocomplete
      label="Search Animals"
      items={fieldState.items}
      inputValue={fieldState.inputValue}
      onOpenChange={onOpenChange}
      onSubmit={onSubmit}
      onInputChange={onInputChange}>
      {item => <Item>{item.name}</Item>}
    </SearchAutocomplete>
  )
}

<ControlledSearchAutocomplete />
```

### Menu trigger behavior

`useComboBoxState` supports three different `menuTrigger` prop values:

* `input` (default): SearchAutocomplete menu opens when the user edits the input text.
* `focus`: SearchAutocomplete menu opens when the user focuses the SearchAutocomplete input.
* `manual`: SearchAutocomplete menu only opens when the user presses the trigger button or uses the arrow keys.

The example below has `menuTrigger` set to `focus`.

```tsx example
<SearchAutocomplete label="Search Animals" menuTrigger="focus">
  <Item key="red panda">Red Panda</Item>
  <Item key="cat">Cat</Item>
  <Item key="dog">Dog</Item>
  <Item key="aardvark">Aardvark</Item>
  <Item key="kangaroo">Kangaroo</Item>
  <Item key="snake">Snake</Item>
</SearchAutocomplete>
```

### Disabled options

You can disable specific options by providing an array of keys to `useComboBoxState`
via the `disabledKeys` prop. This will prevent options with matching keys from being pressable and
receiving keyboard focus as shown in the example below. Note that you are responsible for the styling of disabled options.

```tsx example
<SearchAutocomplete label="Search Animals" disabledKeys={['cat', 'kangaroo']}>
  <Item key="red panda">Red Panda</Item>
  <Item key="cat">Cat</Item>
  <Item key="dog">Dog</Item>
  <Item key="aardvark">Aardvark</Item>
  <Item key="kangaroo">Kangaroo</Item>
  <Item key="snake">Snake</Item>
</SearchAutocomplete>
```

### Asynchronous loading

This example uses the [useAsyncList](../react-stately/useAsyncList.html) hook to handle asynchronous loading
and filtering of data from a server. You may additionally want to display a spinner to indicate the loading
state to the user, or support features like infinite scroll to load more data.

```tsx example
import {useAsyncList} from '@react-stately/data';

function AsyncLoadingExample() {
  let list = useAsyncList({
    async load({signal, filterText}) {
      let res = await fetch(
        `https://swapi.dev/api/people/?search=${filterText}`,
        {signal}
      );
      let json = await res.json();

      return {
        items: json.results
      };
    }
  });

  return (
    <SearchAutocomplete
      label="Search Star Wars Characters"
      items={list.items}
      inputValue={list.filterText}
      onInputChange={list.setFilterText}>
      {(item) => <Item key={item.name}>{item.name}</Item>}
    </SearchAutocomplete>
  );
}
```

## Internationalization

`useSearchAutocomplete` handles some aspects of internationalization automatically.
For example, the item focus, count, and selection VoiceOver announcements are localized.
You are responsible for localizing all labels and option
content that is passed into the autocomplete.
